import React from 'react';
import type { DependentDeductionSectionId, SectionNavStatus } from './dependentDeductionSections';
import { DEPENDENT_DEDUCTION_SECTION_ORDER, SECTION_ACCENT_COLOR } from './dependentDeductionSections';
import { interpolateI18n } from '../utils/interpolateI18n';
import './DependentDeductionSectionMenu.css';

export interface DependentDeductionSectionMenuProps {
  t: (key: string) => string;
  sectionLabelKey: (id: DependentDeductionSectionId) => string;
  sectionStatuses: Record<DependentDeductionSectionId, SectionNavStatus>;
  /** When false (first open), rows are display-only; user starts via footer. */
  allowRowClick: boolean;
  showEditHint: boolean;
  onSelectSection: (id: DependentDeductionSectionId) => void;
}

function isRowClickable(status: SectionNavStatus, allowRowClick: boolean): boolean {
  if (!allowRowClick) return false;
  return status === 'completed' || status === 'available';
}

const DependentDeductionSectionMenu: React.FC<DependentDeductionSectionMenuProps> = ({
  t,
  sectionLabelKey,
  sectionStatuses,
  allowRowClick,
  showEditHint,
  onSelectSection,
}) => {
  return (
    <div className="dep-ded-section-menu">
      <ul className="dep-ded-section-menu__list" role="list">
        {DEPENDENT_DEDUCTION_SECTION_ORDER.map((id, index) => {
          const status = sectionStatuses[id];
          const clickable = isRowClickable(status, allowRowClick);
          const sectionNum = index + 1;
          const prefix = interpolateI18n(t('submitDocuments.dependentDeduction.sectionMenu.sectionPrefix'), {
            n: String(sectionNum),
          });
          const label = t(sectionLabelKey(id));

          return (
            <li key={id} className="dep-ded-section-menu__item-wrap" role="listitem">
              <button
                type="button"
                className={[
                  'dep-ded-section-menu__item',
                  `dep-ded-section-menu__item--${status}`,
                  clickable ? 'dep-ded-section-menu__item--clickable' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={!clickable}
                aria-disabled={!clickable}
                onClick={() => {
                  if (clickable) onSelectSection(id);
                }}
                style={{ '--dep-ded-accent': SECTION_ACCENT_COLOR[id] } as React.CSSProperties}
              >
                <span className="dep-ded-section-menu__accent" aria-hidden />
                <span className="dep-ded-section-menu__label">
                  {prefix} | {label}
                </span>
                {status === 'completed' && (
                  <span className="dep-ded-section-menu__check" aria-hidden>
                    ✓
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {showEditHint && (
        <p className="dep-ded-section-menu__hint">{t('submitDocuments.dependentDeduction.sectionMenu.editHint')}</p>
      )}
    </div>
  );
};

export default DependentDeductionSectionMenu;
